/**
 * Partial provider-coverage disclosure for fan-out tool reports
 * (Claims B-tier 2). Ports the citation-check "Partial scan" pattern:
 * when fewer than the expected providers returned usable responses, say
 * so. It is informational, not alarming, and never implies the brand was
 * absent from providers that simply were not reachable.
 *
 * Pure presentational: renders nothing at full coverage. Callers pass
 * the providers that actually responded (fields fan-out results already carry
 * via usableProviders / providerCoverage / providers[].available).
 */

import type { ResponseCoverage } from '@/lib/tools/response-coverage'

const EXPECTED_PROVIDERS = 4

export function PartialCoverageBanner({
  respondedProviders,
  expectedCount = EXPECTED_PROVIDERS,
  missingProviderReason = 'were unavailable during this scan',
  coverage,
}: {
  /** Providers that returned usable responses in this scan. */
  respondedProviders?: string[]
  /** Total providers the tool attempts (defaults to the 4-engine fan-out). */
  expectedCount?: number
  /** Why omitted providers were not usable; defaults to API unavailability. */
  missingProviderReason?: string
  /** Canonical response-level coverage for Citation and Source Influence. */
  coverage?: ResponseCoverage
}) {
  if (coverage) {
    if (coverage.status === 'complete') return null

    const degradedProviders = coverage.providers.filter(
      provider => provider.role === 'scored' && provider.status !== 'complete'
    )
    const reportOnlyProviders = coverage.providers.filter(
      provider => provider.role === 'report_only'
    )

    return (
      <div
        data-testid='response-coverage-notice'
        className='border px-4 py-3 text-xs text-foreground/80 leading-relaxed'
        style={{
          backgroundColor:
            'color-mix(in srgb, var(--warning-text) 6%, transparent)',
          borderColor:
            'color-mix(in srgb, var(--warning-text) 12%, transparent)',
        }}
      >
        <p>
          <strong style={{ color: 'var(--warning-text)' }}>
            Score withheld:
          </strong>{' '}
          {coverage.usableResponses} of {coverage.expectedResponses} expected
          responses were usable ({coverage.completenessPercent}%).
        </p>
        {degradedProviders.length > 0 && (
          <ul className='mt-1.5 space-y-0.5'>
            {degradedProviders.map(provider => {
              const reasons = [
                provider.failedResponses > 0
                  ? `${provider.failedResponses} failed`
                  : null,
                provider.emptyResponses > 0
                  ? `${provider.emptyResponses} empty`
                  : null,
                provider.unknownResponses > 0
                  ? `${provider.unknownResponses} outcome not recorded`
                  : null,
                provider.unattemptedResponses > 0
                  ? `${provider.unattemptedResponses} not attempted`
                  : null,
              ].filter((reason): reason is string => reason !== null)

              return (
                <li key={provider.provider}>
                  {provider.provider}: {provider.usableResponses}/
                  {provider.expectedResponses} usable
                  {reasons.length > 0 ? ` (${reasons.join(', ')})` : ''}.
                </li>
              )
            })}
          </ul>
        )}
        {(coverage.unknownResponses > 0 || degradedProviders.length === 0) && (
          <p className='mt-1.5 text-muted-foreground'>
            Response gap: failure detail unavailable for this historical run.
          </p>
        )}
        {reportOnlyProviders.length > 0 && (
          <p className='mt-1.5 text-muted-foreground'>
            {reportOnlyProviders.map(provider => provider.provider).join(', ')}{' '}
            {reportOnlyProviders.length === 1 ? 'is' : 'are'} report-only and
            excluded from the score.
          </p>
        )}
        <p className='mt-1.5 text-muted-foreground'>
          Missing responses are unavailable evidence, not evidence that your
          brand was absent.
        </p>
      </div>
    )
  }

  const legacyRespondedProviders = respondedProviders ?? []
  const responded = [...new Set(legacyRespondedProviders)]
  if (responded.length >= expectedCount) return null

  return (
    <div
      data-testid='partial-coverage-banner'
      className='border px-4 py-2.5 text-xs text-foreground/80 leading-relaxed'
      style={{
        backgroundColor:
          'color-mix(in srgb, var(--warning-text) 6%, transparent)',
        borderColor: 'color-mix(in srgb, var(--warning-text) 12%, transparent)',
      }}
    >
      <strong style={{ color: 'var(--warning-text)' }}>
        Partial provider coverage:
      </strong>{' '}
      this result is based on {responded.length} of {expectedCount} providers
      that returned usable responses
      {responded.length > 0 ? <> ({responded.join(', ')})</> : null}. Providers
      not listed {missingProviderReason}. This does not mean your brand is
      absent there.
    </div>
  )
}
